import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import type { FilterComparator } from "@/shared/components/filters/FilterSchema";
import {
  comparatorLabel,
  spokenComparatorLabel,
} from "@/shared/components/filters/operators";
import { Select, type SelectItem } from "@/shared/foundation/primitives";

interface OperatorPickerProps {
  fieldType: FilterDefinition["type"];
  value: FilterComparator;
  onChange: (next: FilterComparator) => void;
  /**
   * The value-bearing comparators to offer — the field's effective set (see
   * `effectiveComparators`).
   */
  operators: readonly FilterComparator[];
  /**
   * The valueless presets, offered after the comparators (see
   * `effectivePresets`). Both lists are per-field rather than per-type, so a
   * field that admits only some of its type's presets — an `identity` column
   * declaring one principal type — gets only those.
   */
  presets: readonly FilterComparator[];
}

/**
 * The operator dropdown: the field's value-bearing comparators, then its
 * presets below a rule. Each reads as `comparatorLabel` words it on this field
 * type. One drawn as a glyph (=, ≠, ≤, …) is named by its spoken phrase
 * instead (`spokenComparatorLabel`), since screen readers narrate the glyphs
 * inconsistently.
 */
export function OperatorPicker({
  fieldType,
  value,
  onChange,
  operators,
  presets,
}: OperatorPickerProps) {
  const items: SelectItem<FilterComparator>[] = [...operators, ...presets].map(
    (operator) => ({
      value: operator,
      label: comparatorLabel(fieldType, operator),
    }),
  );
  return (
    <Select<FilterComparator>
      items={items}
      value={value}
      onValueChange={(next) => {
        if (next !== null) {
          onChange(next);
        }
      }}
    >
      <Select.Trigger aria-label="Operator" />
      <Select.Content>
        {operators.map((operator) => (
          <Select.Item key={operator} value={operator}>
            <OperatorText fieldType={fieldType} operator={operator} />
          </Select.Item>
        ))}
        {presets.length > 0 && <Select.Separator />}
        {presets.map((operator) => (
          <Select.Item key={operator} value={operator}>
            <OperatorText fieldType={fieldType} operator={operator} />
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  );
}

interface OperatorTextProps {
  fieldType: FilterDefinition["type"];
  operator: FilterComparator;
}

/** An option's label, named by its spoken phrase where the two differ. */
function OperatorText({ fieldType, operator }: OperatorTextProps) {
  const label = comparatorLabel(fieldType, operator);
  const spoken = spokenComparatorLabel(fieldType, operator);
  if (label === spoken) {
    return label;
  }
  return (
    <>
      <span aria-hidden="true">{label}</span>
      <span className="sr-only">{spoken}</span>
    </>
  );
}
